import { ModalMessage } from '@zorro/design-system/v2/ModalMessage';
import { Switch } from '@zorro/design-system/v2/Switch';
import { Grid, Stack, Text } from '@zorro/web/design-system';
import { useMonolithQuery, useRoles } from '@zorro/web/ui-utils';
import { useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useAnalytics } from '../Analytics';
import { ZorroSetupFormFields } from '../ZorroSetupForm';

type Props = {
  zorroSetupForm: UseFormReturn<ZorroSetupFormFields>;
  employerId: string;
};

export const FeatureManagementFormInputs = ({
  zorroSetupForm,
  employerId,
}: Props) => {
  const [isStopEmploymentSyncModalOpen, setIsStopEmploymentSyncModalOpen] =
    useState<boolean>(false);
  const { isAgentWithoutSupervisor: isReadonlyMode } = useRoles();
  const { sendEvent } = useAnalytics();
  const { control, watch } = zorroSetupForm;
  const isEmploymentSyncOn = watch('isEmploymentSyncOn');

  const { data: hasEmployeeIdentities } = useMonolithQuery({
    method: 'finchControllerHasEmployeeIdentities',
    params: [employerId],
    enabled: isEmploymentSyncOn,
  });

  return (
    <>
      <Text fw={600} fz="md">
        Feature management
      </Text>
      <Grid>
        <Grid.Col span={{ sm: 12 }}>
          <Stack>
            <Controller
              control={control}
              name="isPartnersHubOn"
              render={({ field: { value, ref: _ref, ...rest } }) => (
                <Switch
                  {...rest}
                  checked={value}
                  label="Partners hub"
                  disabled={isReadonlyMode}
                  w="fit-content"
                  onChange={(event) => {
                    rest.onChange(event);
                    void sendEvent('feature_management_toggle_partners_hub', {
                      employerId,
                      enabled: event.target.checked,
                    });
                  }}
                />
              )}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ sm: 12 }}>
          <Stack>
            <Controller
              control={control}
              name="isPayrollSyncOn"
              render={({ field: { value, ref: _ref, ...rest } }) => (
                <Switch
                  {...rest}
                  checked={value}
                  label="Payroll sync (via Finch)"
                  mt={8}
                  disabled={isReadonlyMode}
                  w="fit-content"
                  onChange={(event) => {
                    rest.onChange(event);
                    void sendEvent('feature_management_toggle_payroll_sync', {
                      employerId,
                      enabled: event.target.checked,
                    });
                  }}
                />
              )}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Stack>
            <Controller
              control={control}
              name="isEmploymentSyncOn"
              render={({ field: { value, ref: _ref, ...rest } }) => (
                <Switch
                  {...rest}
                  checked={value}
                  label="Employment sync (via Finch)"
                  mt={8}
                  disabled={isReadonlyMode}
                  w="fit-content"
                  onChange={(event) => {
                    if (hasEmployeeIdentities && !event.target.checked) {
                      setIsStopEmploymentSyncModalOpen(true);
                      void sendEvent(
                        'feature_management_open_stop_employment_sync_modal',
                        {
                          employerId,
                        }
                      );
                    } else {
                      rest.onChange(event);
                      void sendEvent(
                        'feature_management_toggle_employment_sync',
                        {
                          employerId,
                          enabled: event.target.checked,
                        }
                      );
                    }
                  }}
                />
              )}
            />
          </Stack>
        </Grid.Col>
      </Grid>
      <ModalMessage
        isOpen={isStopEmploymentSyncModalOpen}
        onClose={() => setIsStopEmploymentSyncModalOpen(false)}
        variant="negative"
        title="Stop employee management automation?"
        description="Zorro will stop automatically managing employees. Important: Zorro will still have 'Employment' as part of the permission scope. To remove the permission, instruct the employer admin to re-authenticate."
        primaryLabel="Disable Sync"
        onPrimaryAction={() => {
          void sendEvent('feature_management_confirm_stop_employment_sync', {
            employerId,
          });
          setIsStopEmploymentSyncModalOpen(false);
          zorroSetupForm.setValue('isEmploymentSyncOn', false);
        }}
        secondaryLabel="Cancel"
        onSecondaryAction={() => {
          void sendEvent('feature_management_cancel_stop_employment_sync', {
            employerId,
          });
          setIsStopEmploymentSyncModalOpen(false);
        }}
      />
    </>
  );
};
